import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, ClipboardList, User, GraduationCap, Phone, Mail, 
  Building2, UserCheck, Briefcase, AlertTriangle, ChevronRight,
  Calendar, FileCheck, CheckCircle, Clock, Search, Award
} from 'lucide-react';
import { toast } from 'sonner';
import ZenButton from '../components/ZenButton';
import UserAvatar from '../components/UserAvatar';
import PrerequisValidation from '../components/PrerequisValidation';
import EPMSPValidation from '../components/EPMSPValidation';
import { Badge } from '../components/ui/badge';
import NotificationWidget from '../components/NotificationWidget';
import { TuileGuide } from '../components/guide-bc/TuileGuide';

const DashboardResponsable = ({ user }) => {
  const [centreInfo, setCentreInfo] = useState(null);
  const [stats, setStats] = useState({
    stagiaires: 0, formateurs: 0, tuteurs: 0, entreprises: 0,
    absences_semaine: 0, retards_semaine: 0
  });
  const [stagiaires, setStagiaires] = useState([]);
  const [presences, setPresences] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [profile, setProfile] = useState({
    photo: '', date_naissance: '', diplome: '', annee_diplome: '',
    telephone: user.telephone || '', email: user.email || ''
  });
  const [loading, setLoading] = useState(true);

  // EPMSP overview
  const [epmspData, setEpmspData] = useState([]);
  const [epmspSearch, setEpmspSearch] = useState('');
  // Blocs de compétences overview
  const [bcData, setBcData] = useState([]);
  const [bcSearch, setBcSearch] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const statsRes = await axios.get('/responsable/stats');
      setCentreInfo({
        nom: statsRes.data.centre_nom,
        centre_id: statsRes.data.centre_id,
        google_calendar_code: statsRes.data.google_calendar_code
      });
      setStats(statsRes.data.stats);

      const stagiairesRes = await axios.get('/responsable/stagiaires');
      setStagiaires(stagiairesRes.data || []);

      const presencesRes = await axios.get('/responsable/presences');
      setPresences(presencesRes.data || []);
      generateAlertes(presencesRes.data || [], stagiairesRes.data || []);

      try {
        const profileRes = await axios.get(`/responsables-pedagogiques/${user.user_id}`);
        if (profileRes.data) setProfile(prev => ({ ...prev, ...profileRes.data }));
      } catch (e) {}

      // Load EPMSP overview
      try {
        const epmspRes = await axios.get('/epmsp/all');
        setEpmspData(epmspRes.data || []);
      } catch (e) {}

      // Load Blocs de compétences overview
      try {
        const bcRes = await axios.get('/blocs-competences/all');
        setBcData(bcRes.data || []);
      } catch (e) {}

    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Aucun centre ne vous a encore été assigné. Contactez le centre de formation.');
      } else {
      }
    } finally {
      setLoading(false);
    }
  };

  const generateAlertes = (presencesList, stagiairesList) => {
    const alertesList = [];
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const presencesByStagiaire = {};
    presencesList.forEach(p => {
      const date = new Date(p.date);
      if (date >= weekAgo) {
        if (!presencesByStagiaire[p.stagiaire_id]) presencesByStagiaire[p.stagiaire_id] = [];
        presencesByStagiaire[p.stagiaire_id].push(p);
      }
    });
    Object.entries(presencesByStagiaire).forEach(([stagiaireId, pres]) => {
      const stagiaire = stagiairesList.find(s => s.user_id === stagiaireId);
      const absences = pres.filter(p => p.statut === 'absent').length;
      const retards = pres.filter(p => p.statut === 'retard').length;
      if (absences > 0) alertesList.push({ type: 'absence', stagiaire, message: `${absences} absence(s) cette semaine`, severity: 'high' });
      if (retards > 0) alertesList.push({ type: 'retard', stagiaire, message: `${retards} retard(s) cette semaine`, severity: 'medium' });
    });
    setAlertes(alertesList.slice(0, 10));
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="text-slate-600">Chargement...</div></div>;
  }

  if (!centreInfo?.nom) {
    return (
      <div className="space-y-6" data-testid="dashboard-responsable-no-centre">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
          <Building2 className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Aucun centre assigné</h1>
          <p className="text-slate-600">Vous n'avez pas encore été assigné à un centre de formation.<br />Veuillez contacter l'administration pour être rattaché à un centre.</p>
        </div>
      </div>
    );
  }

  // EPMSP filtered
  const EPREUVES_NAMES = [
    "Séances de découverte VNM à selle",
    "Traction d'un engin tracté",
    "Situation de secours",
    "Séance d'initiation sur support au choix (ESH/PNM/Jet à Bras)"
  ];
  const epmspFiltered = epmspData.filter(s =>
    `${s.prenom} ${s.nom}`.toLowerCase().includes(epmspSearch.toLowerCase())
  );

  // BC filtered
  const BLOCS = [
    { id: "BC1", nom: "Projet d'animation" },
    { id: "BC2", nom: "Valorisation" },
    { id: "BC3", nom: "Séance d'animation" }
  ];
  const bcFiltered = bcData.filter(s =>
    `${s.prenom} ${s.nom}`.toLowerCase().includes(bcSearch.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="dashboard-responsable">
      {/* Header Bandeau Bleu */}
      <div className="bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] rounded-xl p-4 sm:p-6 text-white shadow-lg">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-20 sm:w-24 sm:h-32 bg-white/20 border-2 sm:border-4 border-white/40 rounded-lg overflow-hidden">
                {profile.photo ? (
                  <img src={profile.photo} alt="Photo" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><User className="h-8 w-8 sm:h-12 sm:w-12 text-white/60" /></div>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-white/70 mb-1">Responsable Pedagogique</p>
              <h1 className="text-lg sm:text-2xl md:text-3xl font-bold mb-1 truncate">{user.prenom} {user.nom}</h1>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 text-xs sm:text-sm text-white/90">
                {profile.telephone && <span className="flex items-center gap-1 truncate"><Phone className="h-3 w-3 flex-shrink-0" />{profile.telephone}</span>}
                <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3 flex-shrink-0" />{profile.email || user.email}</span>
              </div>
            </div>
          </div>
          <div className="bg-white/10 rounded-lg px-3 py-2 sm:px-4 sm:py-3">
            <p className="font-semibold flex items-center gap-2 text-sm sm:text-base truncate"><Building2 className="h-4 w-4 flex-shrink-0" />Centre {centreInfo.nom}</p>
          </div>
          <div className="flex gap-3">
            <div className="bg-white/10 rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-center flex-1">
              <p className="text-xl sm:text-3xl font-bold">{stats.stagiaires}</p>
              <p className="text-xs sm:text-sm text-white/80">Stagiaires</p>
            </div>
            <div className="bg-white/10 rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-center flex-1">
              <p className="text-xl sm:text-3xl font-bold">{stats.formateurs}</p>
              <p className="text-xs sm:text-sm text-white/80">Formateurs</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 flex-wrap">
        <a href="https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000051901186" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs sm:text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300" data-testid="referentiel-diplome-btn">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>
          <span className="hidden sm:inline">Referentiel diplome</span>
          <span className="sm:hidden">Ref.</span>
        </a>
        <ZenButton />
      </div>

      <TuileGuide />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <Link to="/responsable/stagiaires" className="bg-white border border-slate-200 rounded-lg p-3 sm:p-5 shadow-sm border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="bg-blue-500/10 p-1.5 sm:p-2 rounded-lg"><Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" /></div>
            <div><p className="text-xl sm:text-2xl font-bold text-[#0F172A]">{stats.stagiaires}</p><p className="text-xs sm:text-sm text-slate-600">Stagiaires</p></div>
          </div>
        </Link>
        <Link to="/responsable/formateurs" className="bg-white border border-slate-200 rounded-lg p-3 sm:p-5 shadow-sm border-l-4 border-l-green-500 hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="bg-green-500/10 p-1.5 sm:p-2 rounded-lg"><GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" /></div>
            <div><p className="text-xl sm:text-2xl font-bold text-[#0F172A]">{stats.formateurs}</p><p className="text-xs sm:text-sm text-slate-600">Formateurs</p></div>
          </div>
        </Link>
        <Link to="/responsable/tuteurs" className="bg-white border border-slate-200 rounded-lg p-3 sm:p-5 shadow-sm border-l-4 border-l-purple-500 hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="bg-purple-500/10 p-1.5 sm:p-2 rounded-lg"><UserCheck className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500" /></div>
            <div><p className="text-xl sm:text-2xl font-bold text-[#0F172A]">{stats.tuteurs}</p><p className="text-xs sm:text-sm text-slate-600">Tuteurs</p></div>
          </div>
        </Link>
        <Link to="/responsable/entreprises" className="bg-white border border-slate-200 rounded-lg p-3 sm:p-5 shadow-sm border-l-4 border-l-orange-500 hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="bg-orange-500/10 p-1.5 sm:p-2 rounded-lg"><Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" /></div>
            <div><p className="text-xl sm:text-2xl font-bold text-[#0F172A]">{stats.entreprises}</p><p className="text-xs sm:text-sm text-slate-600">Entreprises</p></div>
          </div>
        </Link>
        <div className="col-span-2">
          <NotificationWidget />
        </div>
      </div>

      {/* Alertes */}
      {alertes.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-6">
          <h2 className="text-base sm:text-xl font-display font-semibold text-red-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" /> Alertes Presences ({alertes.length})
          </h2>
          <div className="space-y-2">
            {alertes.map((alerte, idx) => (
              <div key={idx} className={`flex items-start sm:items-center justify-between p-2 sm:p-3 rounded-lg gap-2 ${alerte.severity === 'high' ? 'bg-red-100' : 'bg-orange-100'}`}>
                <div className="flex items-start sm:items-center gap-2 sm:gap-3 min-w-0">
                  <AlertTriangle className={`h-4 w-4 flex-shrink-0 mt-0.5 sm:mt-0 ${alerte.severity === 'high' ? 'text-red-600' : 'text-orange-600'}`} />
                  <div className="min-w-0">
                    <p className="font-medium text-[#0F172A] text-sm truncate">{alerte.stagiaire?.user_info?.prenom} {alerte.stagiaire?.user_info?.nom}</p>
                    <p className={`text-xs sm:text-sm ${alerte.severity === 'high' ? 'text-red-600' : 'text-orange-600'}`}>{alerte.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendrier du centre */}
      {centreInfo.google_calendar_code && (
        <div className="bg-white border border-slate-200 rounded-lg p-3 sm:p-6 shadow-sm">
          <h2 className="text-base sm:text-xl font-display font-semibold text-[#0F172A] mb-3 sm:mb-4 flex items-center gap-2">
            <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-[#8DC63F] flex-shrink-0" /> Calendrier
          </h2>
          <div className="rounded-lg overflow-hidden border border-slate-200 [&>iframe]:w-full [&>iframe]:min-h-[300px] sm:[&>iframe]:min-h-[400px]" dangerouslySetInnerHTML={{ __html: centreInfo.google_calendar_code }} />
        </div>
      )}

      {/* Liste des stagiaires du centre */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 sm:p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="text-base sm:text-xl font-display font-semibold text-[#0F172A] flex items-center gap-2">
            <Users className="h-4 w-4 sm:h-5 sm:w-5 text-[#8DC63F] flex-shrink-0" /> <span className="truncate">Stagiaires du Centre</span>
          </h2>
          <Link to="/responsable/stagiaires" className="text-xs sm:text-sm text-[#8DC63F] hover:underline flex-shrink-0">Voir tout</Link>
        </div>
        <div className="space-y-2 sm:space-y-3">
          {stagiaires.slice(0, 5).map((stagiaire) => (
            <Link
              key={stagiaire.user_id}
              to={`/responsable/fiche-stagiaire?id=${stagiaire.user_id}`}
              className="flex items-center justify-between p-2 sm:p-3 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-[#8DC63F] transition-colors cursor-pointer gap-2"
            >
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                <div className="flex-shrink-0">
                  <UserAvatar user={stagiaire.user_info || stagiaire} size="sm" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1 flex-wrap">
                    <p className="font-medium text-[#0F172A] text-sm truncate">{stagiaire.user_info?.prenom} {stagiaire.user_info?.nom}</p>
                    {stagiaire.user_info?.numero_candidat && (
                      <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full border border-slate-200 flex-shrink-0">N°{stagiaire.user_info.numero_candidat}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                    <span className="capitalize">{stagiaire.statut}</span>
                    {stagiaire.has_declaration && (
                      <Badge className="bg-blue-600 text-white text-[10px] px-1 py-0">Declare</Badge>
                    )}
                    {stagiaire.competences_total > 0 && (
                      <Badge className={`text-[10px] px-1 py-0 ${stagiaire.competences_acquired === stagiaire.competences_total ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                        {stagiaire.competences_acquired}/{stagiaire.competences_total}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
            </Link>
          ))}
          {stagiaires.length === 0 && <p className="text-slate-500 text-center py-4 text-sm">Aucun stagiaire dans ce centre</p>}
        </div>
      </div>

      {/* Validation des Prérequis */}
      <PrerequisValidation user={user} />

      {/* Épreuves EPMSP - Validation */}
      <EPMSPValidation user={user} />

      {/* Blocs de Compétences - Vue consultation */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="bc-overview">
        <div className="p-3 sm:p-4 border-b border-slate-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-purple-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0"><Award className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" /></div>
              <div>
                <h2 className="text-base sm:text-lg font-display font-semibold text-[#0F172A]">Blocs de Competences</h2>
                <p className="text-xs sm:text-sm text-slate-500">Vue d'ensemble par stagiaire</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex gap-1.5">
                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] sm:text-xs font-medium">
                  {bcData.filter(s => s.diplome_obtenu).length} diplomes
                </span>
                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-[10px] sm:text-xs font-medium">
                  {bcData.filter(s => !s.diplome_obtenu).length} en cours
                </span>
              </div>
              <div className="relative">
                <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={bcSearch}
                  onChange={e => setBcSearch(e.target.value)}
                  className="pl-7 pr-2 py-1.5 text-xs sm:text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 w-36 sm:w-48"
                  data-testid="bc-search"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto -mx-px">
          <table className="w-full text-xs sm:text-sm min-w-[500px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-2 sm:p-3 font-semibold text-slate-700">Stagiaire</th>
                {BLOCS.map(b => (
                  <th key={b.id} className="text-center p-2 sm:p-3 font-semibold text-slate-700">
                    <span className="px-1 py-0.5 bg-slate-600 text-white rounded text-[9px] sm:text-[10px] mr-0.5">{b.id}</span>
                    <span className="hidden sm:inline">{b.nom}</span>
                  </th>
                ))}
                <th className="text-center p-2 sm:p-3 font-semibold text-slate-700">Diplome</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {bcFiltered.length === 0 ? (
                <tr><td colSpan={BLOCS.length + 2} className="p-6 text-center text-slate-400">Aucun stagiaire trouvé</td></tr>
              ) : bcFiltered.map(stag => (
                <tr key={stag.stagiaire_id} className={`hover:bg-slate-50 ${stag.diplome_obtenu ? 'bg-green-50/30' : ''}`}>
                  <td className="p-2 sm:p-3 font-medium text-[#0F172A] whitespace-nowrap text-xs sm:text-sm">{stag.prenom} {stag.nom}</td>
                  {BLOCS.map(b => {
                    const val = stag.blocs_competences?.[b.id];
                    const isOk = val && (val.statut === 'valide' || val.statut === 'equivalence');
                    const isRat = val && val.statut === 'rattrapage';
                    return (
                      <td key={b.id} className="text-center p-2 sm:p-3">
                        {isOk ? (
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        ) : isRat ? (
                          <Clock className="h-5 w-5 text-orange-400 mx-auto" />
                        ) : (
                          <span className="w-5 h-5 rounded-full border-2 border-slate-200 block mx-auto" />
                        )}
                      </td>
                    );
                  })}
                  <td className="text-center p-2 sm:p-3">
                    {stag.diplome_obtenu ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">Obtenu</span>
                    ) : (
                      <span className="text-xs text-slate-500">{stag.validated_count}/{stag.total_bc}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardResponsable;
